/**
 * Detects URLs, line breaks, and tables marked by the backend and converts them to appropriate HTML elements
 * @param {string} text - The text to process
 * @returns {Array} - Array of React elements
 */
import React from 'react';

export const convertUrlsToLinks = (text) => {
  if (!text) return text;
  
  // First, handle URLs
  const processUrls = (content) => {
    if (typeof content !== 'string') return content;
    if (!content.includes('[URL]')) return content;
    
    const urlRegex = /\[URL\](.*?)\[\/URL\]/g;
    const parts = content.split(urlRegex);
    const result = [];
    
    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        // Regular text
        if (parts[i]) result.push(parts[i]);
      } else {
        // URL - create a link
        result.push(
          <a 
            key={`url-${i}`} 
            href={parts[i]} 
            target="_blank" 
            rel="noopener noreferrer"
            className="message-link"
          >
            {parts[i]}
          </a>
        );
      }
    }
    
    return result;
  };
  
  // Process line breaks
  const processLineBreaks = (content) => {
    if (!content) return content;
    if (typeof content === 'string' && !content.includes('[BR]')) return content;
    
    if (Array.isArray(content)) {
      // Process each item in the array
      const result = [];
      for (let i = 0; i < content.length; i++) {
        const item = content[i];
        if (typeof item !== 'string' || !item.includes('[BR]')) {
          result.push(item);
          continue;
        }
        
        const brParts = item.split(/\[BR\]/g);
        for (let j = 0; j < brParts.length; j++) {
          if (brParts[j]) result.push(brParts[j]);
          if (j < brParts.length - 1) result.push(<br key={`br-${i}-${j}`} />);
        }
      }
      return result;
    } else {
      // Process a single string
      const brParts = content.split(/\[BR\]/g);
      if (brParts.length === 1) return content;
      
      const result = [];
      for (let i = 0; i < brParts.length; i++) {
        if (brParts[i]) result.push(brParts[i]);
        if (i < brParts.length - 1) result.push(<br key={`br-${i}`} />);
      }
      return result;
    }
  };
  
  // Process tables
  const processTables = (content) => {
    if (!content) return content;
    
    // Handle array content (from previous processing)
    if (Array.isArray(content)) {
      const result = [];
      for (let i = 0; i < content.length; i++) {
        const item = content[i];
        if (typeof item !== 'string' || !item.includes('[TABLE]')) {
          result.push(item);
          continue;
        }
        
        // Process this string item for tables
        const processed = processTableString(item);
        if (Array.isArray(processed)) {
          result.push(...processed);
        } else {
          result.push(processed);
        }
      }
      return result;
    } else if (typeof content === 'string') {
      // Handle string content
      return processTableString(content);
    }
    
    return content;
  };
  

  
  // Helper function to process a single string for tables
  const processTableString = (str) => {
    if (!str.includes('[TABLE]')) return str;
    
    const tableRegex = /\[TABLE\](.*?)\[\/TABLE\]/gs;
    const tableMatches = str.match(tableRegex);
    
    if (!tableMatches) return str;
    
    // Instead of splitting and keeping all parts, we'll replace the table tags with React elements
    const parts = [];
    let lastIndex = 0;
    let tableIndex = 0;
    
    // Use exec to get positions of matches
    const regex = new RegExp(tableRegex);
    let match;
    
    while ((match = regex.exec(str)) !== null) {
      // Add text before the table
      if (match.index > lastIndex) {
        parts.push(str.substring(lastIndex, match.index));
      }
      
      // Process the table
      const tableContent = match[1];
      const rows = tableContent.match(/\[TR\](.*?)\[\/TR\]/gs) || [];
      
      const tableRows = rows.map((row, rowIndex) => {
        const rowContent = row.replace(/\[TR\]|\[\/TR\]/g, '');
        const cells = rowContent.match(/\[TD\](.*?)\[\/TD\]/gs) || [];
        
        const tableCells = cells.map((cell, cellIndex) => {
          const cellContent = cell.replace(/\[TD\]|\[\/TD\]/g, '');
          // Process URLs in cell content
          const processedContent = cellContent.includes('[URL]') ? processUrls(cellContent) : cellContent;
          return (
            <td key={`cell-${tableIndex}-${rowIndex}-${cellIndex}`} className="message-table-cell">
              {processedContent}
            </td>
          );
        });
        
        return (
          <tr key={`row-${tableIndex}-${rowIndex}`} className="message-table-row">
            {tableCells}
          </tr>
        );
      });
      
      parts.push(
        <table key={`table-${tableIndex}`} className="message-table">
          <tbody>
            {tableRows}
          </tbody>
        </table>
      );
      
      lastIndex = match.index + match[0].length;
      tableIndex++;
    }
    
    // Add any remaining text after the last table
    if (lastIndex < str.length) {
      parts.push(str.substring(lastIndex));
    }
    
    return parts;
  };
  
  // Process the content in sequence: Tables -> Line Breaks -> URLs
  // This order ensures proper nesting of elements
  let result = text;
  
  // Check if the content contains any formatting tags
  if (typeof result === 'string') {
    if (result.includes('[TABLE]')) {
      result = processTables(result);
    }
    if (result.includes('[BR]') || (Array.isArray(result) && result.some(item => typeof item === 'string' && item.includes('[BR]')))) {
      result = processLineBreaks(result);
    }
    if (result.includes('[URL]') || (Array.isArray(result) && result.some(item => typeof item === 'string' && item.includes('[URL]')))) {
      result = processUrls(result);
    }
  }
  
  return result;
};
